import { Sync } from '@material-ui/icons';
import { ConfirmationResult } from 'firebase/auth';
import { Formik } from 'formik';
import _ from 'lodash';
import React, {
  createRef,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  FocusEvent,
  ChangeEvent,
} from 'react';
import { useRouter } from 'next/router';
import { events, getAnalyticsClient } from '@/lib/analytics';
import { isAmazon, linkAmazonAccount } from '@/src/helpers/amazon';
import {
  getPhoneAuthErrorMessage,
  OtpSessionConsumedError,
  RECAPTCHA_WRAPPER_ID,
  rollbackFalconLinkedUserAfterFailedRedeem,
  rollbackRegistrationAuth,
  sendPhoneOtp,
  switchToAnonymousForFalconRedeem,
  verifyPhoneOtpAndLinkEmail,
} from '@/src/helpers/phoneAuth';
import Storyblok from '../../lib/storyblok';
import { auth } from '../../lib/firebase';
import { useActionPage, useConfig, useErrorPage, usePromoCodeDetailsFromQuery } from '../../lib/hooks';
import { RedeemAfterRegisterError, registerRetailUserRequest } from '../../src/helpers/enrollmentClient';
import isPhoenixEnrollmentEnabled, { isPhoenixUberEnrollmentEnabled } from '../../src/helpers/isPhoenixEnrollmentEnabled';
import { redeemPromotionCodeRequest, redeemPromotionRequest } from '../../src/helpers/promotionClient';
import { getVehicleMakes, getVehicleModelsForMakeAltId } from '@/lib/vehicles';
import { RegisterViewExtendParams } from '../RegisterViewExtend';
import FormikCheckbox from '../shared/FormikCheckbox';
import FormikDropdown from '../shared/FormikDropdown';
import FormikTextInput from '../shared/FormikTextInput';
import OTPDialog from './OTPDialog';
import { Button, StyledForm } from './RegisterForm.styled';
import createValidationSchema from './validations';
import { generateQaAttr, generateQaAttrFromBlok } from '../../src/helpers/qa-attribute';
import {
  isEmailRegistered,
  ErrorMessage,
  EmailStatus,
  getConsentConditionsDefaultValues,
  getOmittableConsentConditionsKeys,
  createConsentConditionsValidation,
  ConsentConditionCheckbox,
} from './shared';

interface VehicleMake {
  value: string;
  name: string;
}

interface RegisterFormProps {
  id?: string;
  className?: string;
  blok: RegisterViewExtendParams & {
    terms_conditions_copy?: string;
    vehicle_models?: string;
    default_vehicle_year?: any;
  };
  setView: (view: string) => void;
}

const RegisterForm = (props: RegisterFormProps) => {
  const stateRef = createRef<any>();
  const makeRef = createRef<any>();
  const modelRef = createRef<any>();
  const yearRef = createRef<any>();
  const [loading, setLoading] = useState<boolean>();
  const [make, setMake] = useState<string>('');
  const [model, setModel] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const config = useConfig();
  const promoCodeDetails = usePromoCodeDetailsFromQuery();
  const errorPage = useErrorPage();
  const actionPage = useActionPage();
  const router = useRouter();
  const analytics = getAnalyticsClient();
  const [emailStatus, setEmailStatus] = useState<EmailStatus>(EmailStatus.pendingValidation);
  const [phoneAlreadyRegistered, setPhoneAlreadyRegistered] = useState(false);
  const [selectedMakeAltId, setSelectedMakeAltId] = useState<string>('');
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpPhone, setOtpPhone] = useState('');
  const confirmationRef = useRef<ConfirmationResult | null>(null);
  const pendingValuesRef = useRef<Record<string, unknown> | null>(null);

  const makeOptions = useMemo(() => getVehicleMakes(), []);

  const modelOptions = useMemo(() => {
    let options = getVehicleModelsForMakeAltId(selectedMakeAltId);

    if (props.blok.vehicle_models) {
      const allowedModels = props.blok.vehicle_models.split(',');
      options = options.filter((model) => model.name && _.includes(allowedModels, model.name));
    }

    return options;
  }, [props.blok.vehicle_models, selectedMakeAltId]);

  const validationSchema = useMemo(
    () => createValidationSchema(createConsentConditionsValidation(props.blok?.consent_conditions || [])),
    [props.blok.consent_conditions],
  );

  const [defaultVehicleMake, setDefaultVehicleMake] = useState<VehicleMake | null>(null);

  useEffect(() => {
    if (props.blok.selected_vehicle_make && _.isEmpty(defaultVehicleMake)) {
      _.map(makeOptions, (make) => {
        if (make.value === props.blok.selected_vehicle_make) {
          setDefaultVehicleMake(make);
        }
      });
    }
  }, [defaultVehicleMake, makeOptions, setDefaultVehicleMake, props.blok.selected_vehicle_make]);

  const startYear = 2000;
  const yearOptions = Array.from(Array(new Date().getFullYear() - startYear + 2).keys())
    .map((i) => {
      return { value: String(startYear + i), name: String(startYear + i) };
    })
    .reverse();

  const handleRedeemSuccess = useCallback(async () => {
    isEmailRegistered?.cache?.clear?.();

    if (props.blok.confirmation_route && props.blok.confirmation_route.url) {
      actionPage(props.blok.confirmation_route.url);
    } else {
      if (isAmazon(router.query)) {
        try {
          await linkAmazonAccount({
            email: formRef.current.values.email,
            password: formRef.current.values.password,
            query: router.query,
          });
          actionPage('/confirmation', 'login');
          return;
        } catch (error) {
          console.error('Failed to link Amazon account', { error });
          errorPage('Failed to link Amazon account. Please try to login');
          props.setView('login');
          return;
        }
      }
      actionPage('finish-registration');
    }
  }, [actionPage, errorPage, props, router.query]);

  const formRef = useRef<any>();

  type RegisterSubmitValues = {
    confirmPassword?: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    terms: boolean;
    [key: string]: unknown;
  };

  // Legacy Falcon: single redeemPromotion enrollment mutation.
  const handleSubmit = useCallback(
    async ({ confirmPassword, ...contractInput }: RegisterSubmitValues) => {
      const input = {
        promotionCode: promoCodeDetails?.promotionCode,
        promotionRef: config.promotion.ref,
        planCode: props.blok.plan_code,
        platformName: props.blok.platform_name,
        ...contractInput,
      };

      let softFailed = false;
      try {
        const result = await redeemPromotionRequest(input);
        if (!result.success) {
          softFailed = true;
          const message = result.message || 'Enrollment failed. Please try again.';
          errorPage(message);
          throw new Error(message);
        }
        await handleRedeemSuccess();
      } catch (err) {
        if (!softFailed) {
          errorPage((err as Error).message || '');
        }
        throw err;
      }
    },
    [
      config.promotion.ref,
      errorPage,
      handleRedeemSuccess,
      promoCodeDetails,
      props.blok.plan_code,
      props.blok.platform_name,
    ],
  );

  // Phoenix (post-OTP): phone+email Firebase user already exists → registerRetailUser(firebaseId) → redeemPromotionCode.
  const handleSubmitPhoenix = useCallback(
    async (values: RegisterSubmitValues) => {
      const user = auth.currentUser;
      if (!user?.uid || user.isAnonymous) {
        throw new Error('Phone verification is required before registration.');
      }

      let retailUserCreated = false;

      try {
        await user.getIdToken(true);

        await registerRetailUserRequest({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          firebaseId: user.uid,
          address: {
            addressLine1: values.address,
            administrativeArea: values.state,
            countryCode: 'USA',
            locality: values.city,
            postalCode: values.postalCode,
          },
          // planIds are account plan UUIDs — not Storyblok plan_code. Omit for enrollment;
          // the promo redeem path attaches the promotional plan.
          termsAccepted: values.terms,
        });
        retailUserCreated = true;

        // Refresh claims after account creation so redeemPromotionCode sees isAccountOwner.
        await user.getIdToken(true);

        const result = await redeemPromotionCodeRequest({
          origin: 'PORTAL',
          promotionCode: promoCodeDetails?.promotionCode || '',
          promotionRef: promoCodeDetails?.promotionRef || config.promotion.ref,
        });

        if (!result.id) {
          throw new Error('Redemption failed. Please try again.');
        }

        await handleRedeemSuccess();
      } catch (err) {
        // registerRetailUser does not upsert — keep Firebase if the retail user was created.
        const error =
          retailUserCreated && !(err instanceof RedeemAfterRegisterError) ? new RedeemAfterRegisterError(err) : err;
        errorPage(error instanceof Error ? error.message : 'Enrollment failed. Please try again.');
        throw error;
      }
    },
    [config.promotion.ref, errorPage, handleRedeemSuccess, promoCodeDetails],
  );

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    make: props.blok.selected_vehicle_make,
    model: '',
    year: props.blok.default_vehicle_year,
    password: '',
    confirmPassword: '',
    subscribe: true,
    terms: false,
    subscriptionCopy: props.blok.subscriptionCopy,
    ...getConsentConditionsDefaultValues(props.blok?.consent_conditions || []),
  };

  const handleChange = useCallback(({ name, value }, type) => {
    switch (type) {
      case 'make':
        setMake(value);
        setModel('');
        setYear('');
        setSelectedMakeAltId(name);
        break;
      case 'model':
        setModel(value);
        setYear('');
        break;
      case 'year':
        setYear(value);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    if (!_.isEmpty(defaultVehicleMake) && defaultVehicleMake?.value !== make) {
      handleChange(defaultVehicleMake, 'make');
      formRef.current.values.make = defaultVehicleMake?.value;
    }
  }, [defaultVehicleMake, make, handleChange]);

  const closeOtpDialog = useCallback(() => {
    setOtpOpen(false);
    setOtpPhone('');
    confirmationRef.current = null;
    pendingValuesRef.current = null;
    setLoading(false);
  }, []);

  const handleRegister = async (values: any) => {
    analytics.track(events.clickRegisterButton, {
      host: window.location.host,
      pathname: window.location.pathname,
      email: values.email,
    });

    setLoading(true);

    const newValue = _.omit(values, getOmittableConsentConditionsKeys(props.blok?.consent_conditions || []));

    try {
      const confirmation = await sendPhoneOtp(values.phone);
      confirmationRef.current = confirmation;
      pendingValuesRef.current = newValue as Record<string, unknown>;
      setOtpPhone(values.phone);
      setOtpOpen(true);
      setLoading(false);
    } catch (err) {
      console.error('Failed to send phone OTP', err);
      errorPage(getPhoneAuthErrorMessage(err, 'Failed to send verification code. Please try again.'));
      setLoading(false);
    }
  };

  const blockEmailAsRegistered = useCallback(() => {
    isEmailRegistered?.cache?.clear?.();
    setEmailStatus(EmailStatus.registered);
  }, []);

  /** Account conflicts: close dialog and update UI. Returns true when the error was handled. */
  const handleOtpLinkError = useCallback(
    (error: unknown): boolean => {
      if (!(error instanceof OtpSessionConsumedError)) {
        return false;
      }

      confirmationRef.current = null;
      if (!error.isAccountConflict) {
        return false;
      }

      closeOtpDialog();
      if (error.conflictKind === 'email') {
        blockEmailAsRegistered();
      } else if (error.conflictKind === 'phone') {
        setPhoneAlreadyRegistered(true);
      }
      return true;
    },
    [blockEmailAsRegistered, closeOtpDialog],
  );

  /** Falcon switches to anonymous before redeem (EXT-325), then runs legacy redeem. */
  const enrollFalconAfterLink = useCallback(
    async (values: RegisterSubmitValues) => {
      try {
        await switchToAnonymousForFalconRedeem();
      } catch (sessionError) {
        errorPage(getPhoneAuthErrorMessage(sessionError, 'Unable to prepare enrollment. Please try again.'));
        throw sessionError;
      }
      await handleSubmit(values);
    },
    [errorPage, handleSubmit],
  );

  const cleanupFailedEnrollment = useCallback(
    async (params: {
      error: unknown;
      falconNeedsReauthTeardown: boolean;
      email: string;
      password: string;
      isNewPhoneUser: boolean;
    }) => {
      const { error, falconNeedsReauthTeardown, email, password, isNewPhoneUser } = params;

      if (error instanceof RedeemAfterRegisterError) {
        blockEmailAsRegistered();
        return;
      }

      try {
        if (falconNeedsReauthTeardown) {
          await rollbackFalconLinkedUserAfterFailedRedeem(email, password, isNewPhoneUser);
        } else if (auth.currentUser && !auth.currentUser.isAnonymous) {
          await rollbackRegistrationAuth(isNewPhoneUser);
        }
      } catch (restoreError) {
        console.error('Failed to restore anonymous Firebase session', restoreError);
        blockEmailAsRegistered();
      }

      isEmailRegistered?.cache?.clear?.();
    },
    [blockEmailAsRegistered],
  );

  const handleOtpVerified = useCallback(
    async (otp: string) => {
      // Post-OTP outcomes (after phone + email/password are linked on one Firebase UID):
      // 1. Success — keep the linked Firebase user (phone persists). Falcon goes anonymous for redeem.
      // 2. Falcon failure — no backend account; re-auth and delete/unlink so register can retry.
      // 3. Phoenix RedeemAfterRegisterError — retail user exists; keep Firebase user and send to login.

      const pendingValues = pendingValuesRef.current;
      if (!confirmationRef.current || !pendingValues) {
        throw new Error('Verification session expired. Please try again.');
      }

      const email = String(pendingValues.email || '');
      const password = String(pendingValues.password || '');
      if (!email || !password) {
        throw new Error('Missing email or password for account linking.');
      }

      let isNewPhoneUser = false;
      try {
        const linked = await verifyPhoneOtpAndLinkEmail(confirmationRef.current, otp, email, password);
        isNewPhoneUser = linked.isNewPhoneUser;
        isEmailRegistered?.cache?.clear?.();
      } catch (error) {
        if (handleOtpLinkError(error)) {
          return;
        }
        throw error;
      }

      // Set before Falcon switch so a partial signOut still gets re-auth teardown.
      let falconNeedsReauthTeardown = false;
      try {
        const values = pendingValues as RegisterSubmitValues;
        if (
          isPhoenixEnrollmentEnabled() ||
          (isPhoenixUberEnrollmentEnabled() && props.blok.platform_name?.toLowerCase() === 'uber')
        ) {
          await handleSubmitPhoenix(values);
        } else {
          falconNeedsReauthTeardown = true;
          await enrollFalconAfterLink(values);
        }
        closeOtpDialog();
      } catch (error) {
        await cleanupFailedEnrollment({
          error,
          falconNeedsReauthTeardown,
          email,
          password,
          isNewPhoneUser,
        });
        closeOtpDialog();
        throw error;
      }
    },
    [
      cleanupFailedEnrollment,
      closeOtpDialog,
      enrollFalconAfterLink,
      handleOtpLinkError,
      handleSubmitPhoenix,
      props.blok.platform_name,
    ],
  );

  const handleOtpResend = useCallback(async () => {
    if (!otpPhone) {
      throw new Error('Missing phone number for resend');
    }
    const confirmation = await sendPhoneOtp(otpPhone);
    confirmationRef.current = confirmation;
  }, [otpPhone]);

  let displayedModelOptions = modelOptions;

  if (make === 'BMW' && !_.isEmpty(displayedModelOptions)) {
    const filterArray = [
      '1 Active E',
      'ActiveE',
      'Vision',
      'Megacity I3',
      '530e',
      '740e xDrive iPerformance',
      '740e xDrive Plug-In Hybrid',
    ];
    displayedModelOptions = _.filter(displayedModelOptions, (option) => {
      return !filterArray.includes(option.value);
    });

    displayedModelOptions = _.map(displayedModelOptions, (option) => {
      if (option.value === 'I3' || option.value === 'I8') {
        return { ...option, value: option.value.toLowerCase() };
      }
      return option;
    });

    displayedModelOptions.sort((a: VehicleMake, b: VehicleMake) => {
      if (
        a.value.charAt(0) === a.value.charAt(0).toLowerCase() &&
        _.isNaN(Number(a.value.charAt(0))) &&
        b.value.charAt(0) === b.value.charAt(0).toUpperCase()
      ) {
        return -1;
      }
      if (
        a.value.charAt(0) === a.value.charAt(0).toLowerCase() &&
        _.isNaN(Number(a.value.charAt(0))) &&
        !_.isNaN(b.value.charAt(0))
      ) {
        return 1;
      }
      if (
        a.value.charAt(0) === a.value.charAt(0).toUpperCase() &&
        _.isNaN(Number(a.value.charAt(0))) &&
        !_.isNaN(b.value.charAt(0))
      ) {
        return 1;
      }
      if (a.value == b.value) return 0;
      return a.value < b.value ? -1 : 1;
    });
  }

  const getEmailStatus = async (input: HTMLInputElement) => {
    const value = input.value;

    if (value) {
      const registered = await isEmailRegistered(value);
      setEmailStatus(registered ? EmailStatus.registered : EmailStatus.unregistered);
    }
  };

  const onEmailBlur = (event: FocusEvent<HTMLInputElement>) => {
    formRef.current.handleBlur(event);
    getEmailStatus(event.target);
  };

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isFocused = document.activeElement === event.target;

    formRef.current.handleChange(event);

    if (emailStatus !== EmailStatus.pendingValidation) {
      setEmailStatus(EmailStatus.pendingValidation);
    }

    if (!isFocused) {
      getEmailStatus(event.target);
    }
  };

  const onPhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    formRef.current.handleChange(event);
    if (phoneAlreadyRegistered) {
      setPhoneAlreadyRegistered(false);
    }
  };

  const isRegistered = emailStatus === EmailStatus.registered;
  const isUnregistered = emailStatus === EmailStatus.unregistered;

  return (
    <Fragment>
      {/* Empty wrapper; phoneAuth mounts the reCAPTCHA container inside it */}
      <div id={RECAPTCHA_WRAPPER_ID} />
      <OTPDialog
        open={otpOpen}
        phoneNumber={otpPhone}
        onVerified={handleOtpVerified}
        onResend={handleOtpResend}
        onCancel={closeOtpDialog}
      />
      <Formik
        innerRef={formRef}
        onSubmit={handleRegister}
        validationSchema={validationSchema}
        initialValues={initialValues}
        validateOnMount={true}
        {...generateQaAttrFromBlok(props.blok)}
      >
        {(formik) => (
          <StyledForm>
            <h3>Account Details</h3>
            <FormikTextInput className="half" name="firstName" label="First Name*" {...generateQaAttr('first-name')} />
            <FormikTextInput className="half" name="lastName" label="Last Name*" {...generateQaAttr('last-name')} />
            <FormikTextInput
              className="half"
              name="email"
              label="Email*"
              onBlur={onEmailBlur}
              onChange={onEmailChange}
              error={isRegistered && 'This email address already exists in our system.'}
              {...generateQaAttr('email')}
            />
            <FormikTextInput
              className="half"
              name="phone"
              type="tel"
              label="Contact Number*"
              error={phoneAlreadyRegistered && 'This phone number is already registered with an email account.'}
              onChange={onPhoneChange}
              {...generateQaAttr('phone')}
            />

            <h3>Address</h3>
            <FormikTextInput name="address" label="Street Address*" {...generateQaAttr('address')} />
            <FormikTextInput className="half" name="city" label="City*" {...generateQaAttr('city')} />
            <FormikDropdown
              className="quarter"
              name="state"
              label="State*"
              {...generateQaAttr('state')}
              ref={stateRef}
            />
            <FormikTextInput className="quarter" name="postalCode" label="Zip*" {...generateQaAttr('postal-code')} />

            <h3>Vehicle Information</h3>
            {!_.isEmpty(defaultVehicleMake) ? (
              <FormikTextInput
                className="half"
                name="make"
                label="Make*"
                {...generateQaAttr('vehicle-make')}
                value={defaultVehicleMake?.value}
                ref={makeRef}
                disabled
              />
            ) : (
              <FormikDropdown
                className="half"
                name="make"
                label="Make*"
                value={make}
                onChange={(value: any) => handleChange(value, 'make')}
                options={makeOptions}
                {...generateQaAttr('vehicle-make-dropdown')}
                ref={makeRef}
              />
            )}
            <FormikDropdown
              value={model}
              className="quarter"
              disabled={!make}
              name="model"
              label="Model*"
              onChange={(value: any) => handleChange(value, 'model')}
              options={displayedModelOptions}
              {...generateQaAttr('vehicle-model-dropdown')}
              ref={modelRef}
            />
            <FormikDropdown
              value={year || props.blok.default_vehicle_year || ''}
              className="quarter"
              disabled={!make}
              name="year"
              label="Year*"
              onChange={(value: any) => handleChange(value, 'year')}
              options={yearOptions as any}
              {...generateQaAttr('vehicle-year-dropdown')}
              ref={yearRef}
            />

            <h3>Security</h3>
            <FormikTextInput
              className="half"
              name="password"
              label="Password*"
              type="password"
              {...generateQaAttr('password')}
            />
            <FormikTextInput
              className="half"
              name="confirmPassword"
              label="Confirm Password*"
              type="password"
              {...generateQaAttr('confirm-password')}
            />

            <h6>
              Password requirements: 8-24 characters, including at least 1 number, 1 uppercase, 1 lowercase & 1 special
              character
            </h6>
            <hr />
            {props.blok.terms_conditions_copy ? (
              <FormikCheckbox name="terms" {...generateQaAttr('terms-checkbox')}>
                <div dangerouslySetInnerHTML={{ __html: props.blok.terms_conditions_copy }} />
              </FormikCheckbox>
            ) : (
              <FormikCheckbox name="terms" {...generateQaAttr('terms-checkbox')}>
                By checking this box, you acknowledge you have read and agree to be bound by our{' '}
                <a target="_blank" rel="noreferrer" href="https://www.evgo.com/terms-service/" className="underline">
                  Terms of Service
                </a>{' '}
                and our{' '}
                <a target="_blank" rel="noreferrer" href="https://www.evgo.com/privacy-policy/" className="underline">
                  Privacy Policy
                </a>
                . By submitting this form, you acknowledge you are sharing your personal information with EVgo and
                consent to EVgo’s Privacy Notice for California Residents. More detail regarding personal information we
                collect, how we use that information, how we share that information, and your rights and choices can be
                found in our{' '}
                <a target="_blank" rel="noreferrer" href="https://www.evgo.com/privacy-policy/" className="underline">
                  Privacy Policy
                </a>
                .
              </FormikCheckbox>
            )}
            <FormikCheckbox name="subscribe" {...generateQaAttr('subscribe-checkbox')}>
              {props.blok.subscriptionCopy}
            </FormikCheckbox>

            {props.blok?.consent_conditions
              ?.filter((v) => !!v.id)
              ?.map((condition) => (
                <ConsentConditionCheckbox
                  key={condition._uid}
                  name={condition.id}
                  {...generateQaAttr(`${condition.id}-checkbox`)}
                  wrapLabel={false}
                >
                  <span
                    className="condition"
                    dangerouslySetInnerHTML={{ __html: Storyblok.richTextResolver.render(condition.description) }}
                  />
                </ConsentConditionCheckbox>
              ))}

            <div className="submit-container">
              <Button
                type="submit"
                disabled={!!loading || !formik.isValid || !isUnregistered || phoneAlreadyRegistered}
              >
                <span className={loading ? 'loading' : ''}>{props.blok.register_submit_button_copy}</span>
                {loading ? <Sync className="spinner" /> : null}
              </Button>

              {isRegistered && (
                <ErrorMessage>
                  This email address already exists in our system.{' '}
                  <span className="action" onClick={() => props.setView('login')}>
                    Log in here
                  </span>{' '}
                  with your email and password.
                </ErrorMessage>
              )}

              {phoneAlreadyRegistered && (
                <ErrorMessage>
                  This phone number is already registered with an email account.{' '}
                  <span className="action" onClick={() => props.setView('login')}>
                    Log in here
                  </span>
                  , or use a different phone number to register.
                </ErrorMessage>
              )}

              <p>{props.blok.register_submit_button_subtext}</p>
            </div>
          </StyledForm>
        )}
      </Formik>
    </Fragment>
  );
};

export default RegisterForm;
